import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import { ClientsToolbar } from '../components/ClientsToolbar';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { ClientDialog } from '../components/ClientDialog';
import { useClients } from '../hooks/useClients';
import { useClientsFilters } from '../hooks/useClientsFilters';
import { getClientColumns } from '../adapters/clientColumns';
import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import type { SortingState } from '@/shared/components/custom/list-view/types';

export default function ClientsPage() {
  const { t } = useTranslation('clients');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { search, debouncedSearch, setSearch, activeFiltersCount, resetFilters } =
    useClientsFilters();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>([]);

  const sortBy = sorting[0]?.id;
  const sortAscending = sorting[0] !== undefined ? !sorting[0].desc : undefined;

  const clientsDataSource = useClients(debouncedSearch, pageIndex, pageSize, sortBy, sortAscending);

  const pagination = usePagination({
    totalItems: clientsDataSource.totalCount ?? 0,
    pageIndex,
    onPageChange: setPageIndex,
    pageSize,
    onPageSizeChange: setPageSize,
  });

  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);
    setPageIndex(0);
  };

  const columns = getClientColumns({ t });

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div>
        <ClientsToolbar
          search={search}
          onSearchChange={setSearch}
          activeFiltersCount={activeFiltersCount}
          onResetFilters={resetFilters}
          onCreateClick={() => setIsCreateDialogOpen(true)}
        />

        <ListView
          data={clientsDataSource.data}
          columns={columns}
          getRowId={(client) => client.id}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          pagination={pagination}
        />

        <ClientDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />
      </div>
    </FeatureErrorBoundary>
  );
}
